import React from 'react';
import { Operator, OperatorId } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { OperatorLogo, OPERATOR_CONFIGS } from '../common/BrandLogo';

interface OperatorSelectorProps {
  operators: Operator[];
  selectedId: OperatorId;
  onSelect: (id: OperatorId) => void;
}

export const OperatorSelector: React.FC<OperatorSelectorProps> = ({
  operators,
  selectedId,
  onSelect,
}) => {
  const { t } = useLanguage();

  const displayOperators =
    operators.length > 0
      ? operators
      : [
          { id: 'mpt' as OperatorId, name: 'MPT', package_status: 'open' as const },
          { id: 'atom' as OperatorId, name: 'ATOM', package_status: 'open' as const },
          { id: 'u9' as OperatorId, name: 'U9 (Ooredoo)', package_status: 'open' as const },
          { id: 'mytel' as OperatorId, name: 'Mytel', package_status: 'open' as const },
        ];

  return (
    <div className="grid grid-cols-4 gap-2.5 my-3.5">
      {displayOperators.map((op) => {
        const isSelected = selectedId === op.id;
        const brand = OPERATOR_CONFIGS[op.id] || OPERATOR_CONFIGS.mpt;
        const isClosed = op.package_status !== 'open';

        const statusLabel =
          op.package_status === 'open'
            ? t.operatorStatus.open
            : op.package_status === 'closed_daily_limit'
            ? t.operatorStatus.closed_daily_limit
            : t.operatorStatus.closed_other;

        return (
          <button
            key={op.id}
            type="button"
            onClick={() => onSelect(op.id)}
            title={`${brand.name} • ${statusLabel}`}
            aria-label={`${brand.name} (${statusLabel})`}
            className={`py-2.5 px-1.5 rounded-2xl border transition-all relative flex flex-col items-center justify-center space-y-1.5 touch-target group ${
              isSelected
                ? `bg-surface-card ring-2 shadow-xs ${brand.selectedRing}`
                : 'bg-surface-card/70 border-plum-border hover:border-primary/40 hover:bg-surface-card'
            } ${isClosed ? 'opacity-70' : ''} active:scale-[0.96]`}
          >
            {/* Status dot in the top-right corner */}
            <span
              className={`absolute top-2 right-2 w-2 h-2 rounded-full ring-2 ring-surface-card ${
                op.package_status === 'open'
                  ? 'bg-emerald-400'
                  : op.package_status === 'closed_daily_limit'
                  ? 'bg-amber-400'
                  : 'bg-red-400'
              }`}
            />

            {/* Operator Logo */}
            <OperatorLogo
              id={op.id}
              size="md"
              className={`rounded-xl shadow-xs transition-transform group-hover:scale-105 ${
                isSelected ? 'scale-105' : ''
              }`}
            />

            {/* Operator Name */}
            <span
              className={`text-xs font-bold leading-none transition-colors ${
                isSelected ? 'text-plum-dark' : 'text-plum-muted group-hover:text-plum-dark'
              }`}
            >
              {brand.name.split(' ')[0]}
            </span>
          </button>
        );
      })}
    </div>
  );
};
